import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Popconfirm, Modal, Button, Tooltip, Icon } from 'antd';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import * as actions from '~actions/payment';
import { communicationStatus, propertyListingForm, paymentPlanCategoryType } from '~constants';
import {
  IconIntroTime as IconIntroTimeIcon,
  IconIntroWechat as IconIntroWechatIcon,
  IconIntroPay as IconIntroPayIcon,
  Tick as TickIcon,
  TickWrong as TickWrongIcon,
} from 'common/components/svgs';
import Pagination from '~components/ui/pagination';
import ErrorMessage from '~components/ui/error-message';
import generatePath from '~settings/routing';
import event from '~helpers/mixpanel';
import { decode } from 'js-base64';
import { pageType } from '~constants/pages';

const mapStateToProps = state => {
  const payment = state.payment.get('longtail').toJS();

  return {
    list: payment.paymentPlans.edges || [],
    pageInfo: payment.paymentPlans.pageInfo || null,
    totalCount: payment.paymentPlans.totalCount || 0,
    getListCommunication: payment.getListCommunication || '',
    hasIntegratedProperty: state.auth.getIn(['properties', 'hasIntegratedProperty']),
  };
};

const mapDispatchToProps = dispatch => ({
  getList: params => {
    dispatch(actions.getLongtailPaymentPlans(params));
  },
  deleteLongtailPayment: (params, onSuccess) => {
    dispatch(actions.deleteLongtailPayment(params, onSuccess));
  },
});

@withRouter
@connect(mapStateToProps, mapDispatchToProps)
@withTranslation()
export default class LongtailPaymentListing extends React.Component {
  constructor() {
    super();
    this.state = {
      pageNumber: 1,
      showViewPropertiesModal: false,
      viewProperties: [],
    };
  }

  componentDidMount() {
    this.initialize();

    event('Viewed Payment Plan Page', { 'Current Page Type': pageType.paymentPlanPage });
  }

  calcPaymentPlanHasIntegratedProperty = () =>
    this.props.list?.some(item => item.node.hasIntegratedProperty);

  initialize = () => {
    this.props.getList(
      {
        pageSize: 10,
        pageNumber: 1,
      },
      true,
    );
  };

  onPaginationChange = page => {
    this.setState(
      Object.assign({}, this.state, {
        pageNumber: page,
      }),
      () => {
        this.props.getList({
          pageSize: 10,
          pageNumber: this.state.pageNumber,
        });
      },
    );
  };

  getDeposits = item => item.node.deposits;

  getFees = item => item.node.fees;

  getInstalments = item => item.node.instalments;

  handleEditBtnClick = id => {
    this.props.history.push(generatePath('paymentEnablePayments', {}, { id }));
  };

  handleDeletePayment = paymentId => {
    this.props.deleteLongtailPayment({ id: paymentId }, () => {
      this.props.getList({
        pageSize: 10,
        pageNumber: this.state.pageNumber,
      });
    });
  };

  showViewPropertiesModal = properties => {
    this.setState({
      viewProperties: properties,
      showViewPropertiesModal: true,
    });
  };

  hiddenViewPropertiesModal = () => {
    this.setState({
      viewProperties: [],
      showViewPropertiesModal: false,
    });
  };

  createPaymentPlan = () => {
    this.props.history.push(generatePath('paymentInitialSetup'));
  };

  hasPublishedProperty = propeties =>
    propeties?.some(propety => propety?.status === propertyListingForm.statuses.published);

  isMustPayDepositFee = paymentPlan =>
    paymentPlan?.node?.category === paymentPlanCategoryType.DEPOSIT_IS_REQUIRED;

  render() {
    const { getListCommunication, t, totalCount } = this.props;

    return (
      <div className="long-tail-payment__list">
        <div className="long-tail-payment__header">
          <div className="long-tail-payment__header__info">
            <h2 className="long-tail-payment__header__heading">
              {this.props.t('landlord.payment.long_tail.list.title')}
            </h2>
            <If condition={totalCount !== 0}>
              <p className="long-tail-payment__header__tips">
                {this.props.t('landlord.payment.long_tail.list.tips')}
              </p>
            </If>
          </div>
          <If condition={totalCount !== 0}>
            <Button
              onClick={this.createPaymentPlan}
              type="primary"
              className="long-tail-payment__header__add-btn"
            >
              <PlusOutlined />
              {this.props.t('landlord.payment.long_tail.list.btn.create')}
            </Button>
          </If>
        </div>
        <If
          condition={
            this.props.hasIntegratedProperty && this.calcPaymentPlanHasIntegratedProperty()
          }
        >
          <p className="long-tail-payment__via-api__tips">
            <Icon
              type="info-circle"
              theme="filled"
              style={{ marginRight: 10, fontSize: 16, marginTop: 4 }}
            />
            <div>{this.props.t('landlord.payment.long_tail.list.via_api.tips')}</div>
          </p>
        </If>
        <Choose>
          <When condition={getListCommunication.status === communicationStatus.FETCHING}>
            <span className="loading" />
          </When>
          <When condition={getListCommunication.status !== communicationStatus.IDLE}>
            <ErrorMessage
              type="middle"
              className="try-again--with-border"
              communication={getListCommunication}
              buttonText={this.props.t('landlord.dashboard.error.try_again')}
              buttonFunction={this.initialize}
              defaultErrorMessage={this.props.t('landlord.error.default')}
              clientErrorMessage={this.props.t('landlord.error.default')}
              timeoutMessage={this.props.t('landlord.error.timeout')}
            />
          </When>
          <When condition={totalCount === 0}>
            <div className="long-tail-payment__welcome">
              <h2 className="long-tail-payment__welcome__heading">
                {this.props.t('landlord.payment.welcome.title')}
              </h2>
              <ul className="long-tail-payment__welcome__list">
                <li className="long-tail-payment__welcome__item">
                  <IconIntroTimeIcon className="long-tail-payment__welcome__icon-time" />
                  <span className="long-tail-payment__welcome__summary">
                    {this.props.t('landlord.payment.welcome.desc_1')}
                  </span>
                </li>
                <li className="long-tail-payment__welcome__item">
                  <IconIntroWechatIcon className="long-tail-payment__welcome__icon-wechat" />
                  <span className="long-tail-payment__welcome__summary">
                    {this.props.t('landlord.payment.welcome.desc_2')}
                  </span>
                </li>
                <li className="long-tail-payment__welcome__item">
                  <IconIntroPayIcon className="long-tail-payment__welcome__icon-pay" />
                  <span className="long-tail-payment__welcome__summary">
                    {this.props.t('landlord.payment.welcome.desc_3')}
                  </span>
                </li>
              </ul>
              <Button
                onClick={this.createPaymentPlan}
                type="primary"
                size="large"
                className="long-tail-payment__welcome__button"
              >
                {this.props.t('landlord.payment.welcome.button')}
              </Button>
            </div>
          </When>
          <Otherwise>
            <div className="long-tail-payment__property-list">
              <ul className="long-tail-payment__property-list__table">
                <li className="long-tail-payment__property-list__table-title">
                  <span className="long-tail-payment__property-list__table-cell">
                    {this.props.t('landlord.payment.list.column.payemnt_plan')}
                  </span>
                  <span className="long-tail-payment__property-list__table-cell long-tail-payment__property-list__table-cell--tick">
                    ID
                  </span>
                  <span className="long-tail-payment__property-list__table-cell long-tail-payment__property-list__table-cell--tick">
                    {this.props.t('landlord.payment.list.column.deposits_or_fees')}
                  </span>

                  <span className="long-tail-payment__property-list__table-cell long-tail-payment__property-list__table-cell--tick">
                    {this.props.t('landlord.payment.list.column.rent_payment')}
                  </span>
                  <span className="long-tail-payment__property-list__table-cell">
                    {this.props.t('landlord.payment.list.column.applied_properties')}
                  </span>
                  <span className="long-tail-payment__property-list__table-cell">
                    {this.props.t('landlord.payment.list.column.status')}
                  </span>
                  <span className="long-tail-payment__property-list__table-cell long-tail-payment__property-list__table-cell--actions">
                    {this.props.t('landlord.payment.list.column.actions')}
                  </span>
                </li>
                <For each="item" of={this.props.list}>
                  <li className="long-tail-payment__property-list__table-item" key={item.node.id}>
                    <span className="long-tail-payment__property-list__table-cell">
                      {item.node.name}
                    </span>
                    <span className="long-tail-payment__property-list__table-cell">
                      {JSON.parse(decode(item.node?.id))?.id ?? '-'}
                    </span>
                    <span className="long-tail-payment__property-list__table-cell">
                      <Choose>
                        <When
                          condition={
                            this.isMustPayDepositFee(item) &&
                            (this.getDeposits(item).length > 0 || this.getFees(item).length > 0)
                          }
                        >
                          <TickIcon className="long-tail-payment__property-list__icon-tick" />
                        </When>
                        <When
                          condition={
                            this.isMustPayDepositFee(item) &&
                            !(this.getDeposits(item).length > 0 && this.getFees(item).length > 0)
                          }
                        >
                          <TickWrongIcon />
                        </When>
                        <When
                          condition={
                            !this.isMustPayDepositFee(item) &&
                            (this.getDeposits(item).length > 0 || this.getFees(item).length > 0)
                          }
                        >
                          <TickIcon className="long-tail-payment__property-list__icon-tick" />
                        </When>
                      </Choose>
                    </span>

                    <span className="long-tail-payment__property-list__table-cell">
                      <Choose>
                        <When condition={this.getInstalments(item).length > 0}>
                          <TickIcon className="long-tail-payment__property-list__icon-tick" />
                        </When>
                        <Otherwise>
                          <TickWrongIcon />
                        </Otherwise>
                      </Choose>
                    </span>
                    <span className="long-tail-payment__property-list__table-cell">
                      <If condition={item.node.properties.length > 0}>
                        {item.node.properties.length}
                        <button
                          className="long-tail-payment__property-list__link"
                          onClick={() => {
                            this.showViewPropertiesModal(item.node.properties);
                          }}
                        >
                          <span className="long-tail-payment__property-list__text--keppel">
                            {this.props.t('landlord.payment.long_tail.list.btn.view')}
                          </span>
                        </button>
                      </If>
                    </span>
                    <span className="long-tail-payment__property-list__table-cell">
                      <If condition={item.node?.active}>
                        <span className="long-tail-payment__property-list__text--active-status">
                          {this.props.t('landlord.payment.list.column.status_value.active')}
                        </span>
                      </If>
                      <If condition={!item.node?.active}>
                        <span className="long-tail-payment__property-list__text--inactive-status">
                          {this.props.t('landlord.payment.list.column.status_value.inactive')}
                        </span>
                      </If>
                    </span>
                    <span className="long-tail-payment__property-list__table-cell">
                      <button
                        className="long-tail-payment__property-list__link"
                        onClick={() => {
                          this.handleEditBtnClick(item.node.id);
                        }}
                      >
                        <span className="long-tail-payment__property-list__text--keppel">
                          {this.props.t('landlord.payment.long_tail.list.btn.edit')}
                        </span>
                      </button>
                      <If condition={!item.node.hasIntegratedProperty}>
                        <If condition={this.hasPublishedProperty(item.node.properties)}>
                          <Tooltip
                            placement="topRight"
                            title={t('payment_plan_disabled_delete_btn_tooltip')}
                          >
                            <button className="long-tail-payment__property-list__link">
                              <span
                                className={classNames(
                                  'long-tail-payment__property-list__text--keppel',
                                  'long-tail-payment__property-list__disabled-btn',
                                )}
                              >
                                {t('landlord.payment.long_tail.list.btn.delete')}
                              </span>
                            </button>
                          </Tooltip>
                        </If>
                        <If condition={!this.hasPublishedProperty(item.node.properties)}>
                          <Popconfirm
                            placement="topRight"
                            title={t('landlord.payment.long_tail.delete_instalment_plan.title', {
                              name: item.node.name,
                            })}
                            onConfirm={() => {
                              this.handleDeletePayment(item.node.id);
                            }}
                            okText={t('landlord.payment.long_tail.button.yes')}
                            okType="danger"
                            cancelText={t('landlord.payment.long_tail.button.no')}
                            overlayStyle={{ width: 410 }}
                          >
                            <button className="long-tail-payment__property-list__link">
                              <span className={'long-tail-payment__property-list__text--keppel'}>
                                {t('landlord.payment.long_tail.list.btn.delete')}
                              </span>
                            </button>
                          </Popconfirm>
                        </If>
                      </If>
                    </span>
                  </li>
                </For>
              </ul>
            </div>

            <If condition={Math.ceil(this.props.totalCount / 10) > 1}>
              <div className="dashboard__pagination">
                <Pagination
                  currentPage={this.state.pageNumber}
                  totalPage={Math.ceil(this.props.totalCount / 10)}
                  onPageClick={this.onPaginationChange}
                />
              </div>
            </If>
          </Otherwise>
        </Choose>

        <Modal
          title={t('landlord.payment.long_tail.initial_setup.label.applied_properties')}
          visible={this.state.showViewPropertiesModal}
          onCancel={this.hiddenViewPropertiesModal}
          footer={null}
          centered
          wrapClassName="small-ant-modal"
        >
          <For of={this.state.viewProperties} each="property">
            <p className="long-tail-payment____property-modal__name" key={property.slug}>
              {property.name}
            </p>
          </For>
        </Modal>
      </div>
    );
  }
}

LongtailPaymentListing.propTypes = {
  list: PropTypes.shape({
    communication: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    numPages: PropTypes.number.isRequired,
    payload: PropTypes.array.isRequired,
  }),
  getList: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  t: PropTypes.func.isRequired,
  getListCommunication: PropTypes.object,
  totalCount: PropTypes.number,
  deleteLongtailPayment: PropTypes.func,
};

LongtailPaymentListing.defaultProps = {
  list: {
    communication: {
      status: '',
    },
    numPages: 1,
    payload: [],
  },
  getList: () => {},
  history: {
    push: () => {},
  },
  t: () => {},
  getListCommunication: {},
  totalCount: 0,
  deleteLongtailPayment: () => {},
};
